import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn, hexToCmyk } from "@/lib/utils";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ColorSwatchProps {
  hex: string;
  name: string;
  showCmyk?: boolean;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "responsive";
}

export const ColorSwatch = ({
  hex,
  name,
  showCmyk = true,
  size = "lg",
}: ColorSwatchProps) => {
  const [copied, setCopied] = useState(false);

  const sizeClasses = {
    xs: "w-14 h-14",
    sm: "w-16 h-16",
    md: "w-20 h-20",
    lg: "w-24 h-24",
    xl: "w-28 h-28 lg:w-32 lg:h-32",
    "2xl": "w-32 h-32 lg:w-40 lg:h-40",
    responsive: "w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-32 lg:h-32",
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopied(true);
      toast.success(`Cor copiada: ${hex}`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Erro ao copiar");
    }
  };

  const cmyk = showCmyk ? hexToCmyk(hex) : null;
  const isWhite = hex.toUpperCase() === "#FFFFFF";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex flex-col items-center text-center group cursor-pointer"
          onClick={handleCopy}
        >
          {/* Label */}
          <p className="text-[10px] sm:text-xs font-semibold text-foreground mb-2 sm:mb-3">{name}</p>

          {/* Swatch Box - Premium Glow Effect */}
          <div
            className={cn(
              sizeClasses[size],
              "rounded-xl mb-3 relative",
              "transition-all duration-300 ease-out",
              "group-hover:shadow-veed-md group-hover:-translate-y-0.5",
              isWhite && "ring-2 ring-muted"
            )}
            style={{
              backgroundColor: hex,
              boxShadow: `0 12px 32px -6px ${hex}60`,
            }}
          >
            {/* Hover ring with dynamic color */}
            <div
              className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                boxShadow: `0 0 0 2px ${hex}, 0 16px 40px -8px ${hex}70`,
              }}
            />

            {/* Copy icon on hover (before click) */}
            {!copied && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="bg-black/30 backdrop-blur-sm rounded-lg p-2">
                  <Copy className="w-4 h-4 text-white drop-shadow-lg" />
                </div>
              </div>
            )}

            {/* Check icon after copy */}
            {copied && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/25 backdrop-blur-sm rounded-xl animate-in fade-in-0 zoom-in-95 duration-200">
                <div className="bg-white/90 rounded-full p-1.5">
                  <Check className="w-5 h-5 text-primary drop-shadow-sm" />
                </div>
              </div>
            )}
          </div>

          {/* HEX Code */}
          <p className="text-[11px] sm:text-sm font-mono font-medium text-foreground group-hover:text-primary transition-colors">
            {hex}
          </p>

          {/* CMYK Conversion */}
          {cmyk && (
            <p className="text-[9px] sm:text-[10px] font-mono text-muted-foreground mt-1">
              C:{cmyk.c} M:{cmyk.m} Y:{cmyk.y} K:{cmyk.k}
            </p>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="font-medium">
        <p>Clique para copiar</p>
      </TooltipContent>
    </Tooltip>
  );
};
